"use client";

import React from "react";
import { User, Plus, Upload, Download, FileText, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUniversalEntityPageContext } from "../contexts/UniversalEntityPageContext";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import { FloatingActionButton } from "@/components/ui/navigation/floating-action-button";
import { createEntityActionLabel } from "@/lib/utils/crm/entity-label";
import { EntityTableRefreshButton } from "./EntityTableRefreshButton";

interface EntityPageHeaderProps<T extends BaseEntity> {
  showHeader: boolean;
  CustomHeader?: React.ComponentType<any>;
  displayTitle: string;
  showUserIcon: boolean;
  totalCount?: number;
  onCreateItem: () => void;
  onImportItems: () => void;
  onExportItems: () => void;
  onComposeMessage?: () => void;
  hasComposeModal: boolean;
  hasData: boolean;
  onAdminAction?: () => void;
  adminActionLabel?: string;
  isAdmin?: boolean;
}

function EntityPageHeaderComponent<T extends BaseEntity>({
  showHeader,
  CustomHeader,
  displayTitle,
  showUserIcon,
  totalCount,
  onCreateItem,
  onImportItems,
  onExportItems,
  onComposeMessage,
  hasComposeModal,
  hasData,
  onAdminAction,
  adminActionLabel,
  isAdmin,
}: EntityPageHeaderProps<T>) {
  // Get context to access infinite scroll state
  let context;
  try {
    context = useUniversalEntityPageContext<T>();
  } catch {
    context = null;
  }

  if (!showHeader) {
    return null;
  }

  if (CustomHeader) {
    return (
      <CustomHeader
        displayTitle={displayTitle}
        showUserIcon={showUserIcon}
        totalCount={totalCount}
        onCreateItem={onCreateItem}
        onImportItems={onImportItems}
        onExportItems={onExportItems}
        onComposeMessage={onComposeMessage}
        hasComposeModal={hasComposeModal}
        hasData={hasData}
        onAdminAction={onAdminAction}
        adminActionLabel={adminActionLabel}
        isAdmin={isAdmin}
      />
    );
  }

  return (
    <>
      <div className="mb-2 md:mb-2">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-4 md:px-0">
          {/* Title Row - Title on left, Count on right (mobile), inline (desktop) */}
          <div className="flex items-center justify-between md:gap-2">
            <div className="flex items-center gap-1.5">
              {showUserIcon && <User className="h-4 w-4 text-primary" />}
              <h1 className="text-lg font-semibold text-card-foreground">{displayTitle}</h1>
            </div>

            {totalCount !== undefined && (
              <Badge
                variant="secondary"
                className="h-5 md:h-6 px-2 md:px-2.5 text-xs md:text-sm font-medium"
              >
                {totalCount}
              </Badge>
            )}
          </div>

          {/* Compact Actions - Desktop Only */}
          <div className="hidden md:flex items-center gap-2">
            <Button
              onClick={onCreateItem}
              size="sm"
              className="h-8 px-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-sm"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              New
            </Button>

            <EntityTableRefreshButton />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 font-medium border-gray-300 dark:border-gray-700 hover:bg-card-50" title="More options"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={onImportItems}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onExportItems} disabled={!hasData}>
                  {/* Download, not ExternalLink — this produces a file rather
                      than navigating somewhere. */}
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </DropdownMenuItem>
                {hasComposeModal && onComposeMessage && (
                  <DropdownMenuItem onClick={onComposeMessage}>
                    <FileText className="h-4 w-4 mr-2" />
                    Compose
                  </DropdownMenuItem>
                )}
                {isAdmin && onAdminAction && adminActionLabel && (
                  <DropdownMenuItem onClick={onAdminAction}>
                    <FileText className="h-4 w-4 mr-2" />
                    {adminActionLabel}
                  </DropdownMenuItem>
                )}
                {context && (
                  <>
                    <DropdownMenuSeparator />
                    {/*
                      A real checkbox menu item. This was a DropdownMenuItem
                      containing a bare <input type="checkbox"> with a no-op
                      onChange: React rendered it as a controlled input with no
                      handler, it exposed no accessible name or checked state to
                      assistive tech, and clicking the box itself did nothing
                      (only the row's onClick worked).
                    */}
                    <DropdownMenuCheckboxItem
                      checked={context.infiniteScrollState.infiniteScrollEnabled}
                      onCheckedChange={(checked) =>
                        context.infiniteScrollState.setInfiniteScrollEnabled(Boolean(checked))
                      }
                    >
                      Infinite scroll
                    </DropdownMenuCheckboxItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <FloatingActionButton
        onClick={onCreateItem}
        icon={<Plus className="h-5 w-5" aria-hidden />}
        /*
         * Singular, sentence-case label ("New deal") shown ON the button, not in
         * a hover tooltip: the FAB is touch-only, where hover never fires, and
         * `displayTitle` is the PLURAL list name so the old label read
         * "Create Deals" for an action that creates one deal.
         */
        label={createEntityActionLabel(displayTitle)}
      />
    </>
  );
}

/**
 * Memoized EntityPageHeader component
 * Only re-renders when props change (totalCount, displayTitle, etc.)
 * Prevents re-renders during search/filter operations
 */
export const EntityPageHeader = React.memo(
  EntityPageHeaderComponent
) as typeof EntityPageHeaderComponent;
